import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, ModalError } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { cofnijOperacje, podgladCofniecia } from '@/lib/authoring-api';
import { useI18n } from '@/lib/i18n';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  COFNIĘCIE OPERACJI (15.10.2026) — okno z podglądem zmian odwrotnych.
 *
 *  ⚠️ ZMIANY SKŁADA SERWER ze stanu sprzed operacji, a tu tylko je widać: ten sam podgląd, werdykty
 *  i ostrzeżenia do potwierdzenia co w edytorze bazy. Uzasadnienie jest obowiązkowe, bo cofnięcie
 *  zostaje w rejestrze zmian jak każda inna operacja.
 *
 *  ⚠️ AGENT ODMÓWI, gdy ktoś zmienił te wiersze po cofanej operacji — wtedy cofnięcie przychodzi jako
 *  nieudana operacja z powodem, a baza zostaje bez zmian.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function CofnijOperacje({
  opId,
  groupId,
  onClose,
  onZakolejkowano,
}: {
  opId: string;
  groupId: string;
  onClose: () => void;
  onZakolejkowano: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [uzasadnienie, setUzasadnienie] = useState('');
  const [potwierdzone, setPotwierdzone] = useState<ReadonlySet<string>>(new Set());
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState(false);

  const p = useQuery({ queryKey: ['cofniecie', opId, groupId], queryFn: () => podgladCofniecia(groupId, opId), retry: false });
  const d = p.data;
  const ostrzezenia = d ? [...new Map(d.verdicts.flatMap((v) => v.warnings.filter((w) => w.requiresAck)).map((w) => [w.code, w])).values()] : [];
  const brakPotwierdzen = ostrzezenia.some((w) => !potwierdzone.has(w.code));
  const mozna = d?.mozna === true && d.token !== null && uzasadnienie.trim().length >= 10 && !brakPotwierdzen;

  const zatwierdz = async () => {
    if (!d || d.token === null) return;
    setBlad(null);
    setZapis(true);
    try {
      await cofnijOperacje({ groupId, opId, authorNote: uzasadnienie.trim(), previewToken: d.token, acknowledged: [...potwierdzone] });
      toast.success(t('cofn.zakolejkowano'));
      onZakolejkowano();
      onClose();
    } catch (e) {
      setBlad(e instanceof ApiError ? e.message : t('common.error'));
    } finally {
      setZapis(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('cofn.tytul')} description={t('cofn.opis')} className="w-[min(52rem,calc(100vw-2rem))]">
        {p.isPending ? (
          <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
          </p>
        ) : p.isError || !d ? (
          <ModalError>{p.error instanceof ApiError ? p.error.message : t('common.error')}</ModalError>
        ) : (
          <div className="space-y-3">
            {d.blokady.length > 0 && (
              <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                <p className="font-medium">{t('cofn.blokady')}</p>
                <ul className="list-disc space-y-0.5 pl-4">
                  {d.blokady.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              </div>
            )}

            {d.zmiany.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-card text-left text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="px-3 py-2 font-medium">{t('chg.table')}</th>
                      <th className="px-3 py-2 font-medium">{t('chg.action')}</th>
                      <th className="px-3 py-2 font-medium">{t('chg.key')}</th>
                      <th className="px-3 py-2 font-medium">{t('chg.data')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.zmiany.map((z, i) => {
                      const v = d.verdicts[i];
                      return (
                        <tr key={`${z.zaSeq}-${i}`} className="border-b border-border/60 align-top last:border-b-0">
                          <td className="px-3 py-2 font-mono">{z.tableName}</td>
                          <td className="px-3 py-2">
                            <span className="font-mono font-medium">{z.action}</span>{' '}
                            <span className="text-muted-foreground">{t(`chg.actionName.${z.action}`)}</span>
                          </td>
                          <td className="px-3 py-2 font-mono">{JSON.stringify(z.pk)}</td>
                          <td className="px-3 py-2">
                            <span className="font-mono text-muted-foreground">{z.payload ? JSON.stringify(z.payload) : '—'}</span>
                            {z.uwagi.map((u) => (
                              <p key={u} className="mt-1 text-[var(--warning)]">
                                {u}
                              </p>
                            ))}
                            {v?.errors.map((e) => (
                              <p key={e} className="mt-1 text-destructive">
                                {e}
                              </p>
                            ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {ostrzezenia.map((w) => (
              <label key={w.code} className="flex cursor-pointer items-start gap-2 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs">
                <input
                  type="checkbox"
                  className="mt-0.5 size-3.5 shrink-0 accent-[var(--primary)]"
                  checked={potwierdzone.has(w.code)}
                  onChange={(e) =>
                    setPotwierdzone((s) => {
                      const n = new Set(s);
                      if (e.target.checked) n.add(w.code);
                      else n.delete(w.code);
                      return n;
                    })
                  }
                />
                <span>
                  <AlertTriangle className="mr-1 inline size-3.5" />
                  {w.message}
                </span>
              </label>
            ))}

            {d.mozna && (
              <label className="block space-y-1 text-xs">
                <span className="text-muted-foreground">{t('cofn.uzasadnienie')}</span>
                <textarea
                  id="cofniecie-uzasadnienie"
                  className="min-h-16 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={uzasadnienie}
                  maxLength={300}
                  onChange={(e) => setUzasadnienie(e.target.value)}
                />
              </label>
            )}
            {d.mozna && (
              <Badge variant="neutral" className="text-[11px]">
                {d.tytul}
              </Badge>
            )}
            <ModalError>{blad}</ModalError>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="destructive" disabled={!mozna || zapis} onClick={() => void zatwierdz()}>
            {zapis ? <Loader2 className="animate-spin" /> : <Undo2 />}
            {t('cofn.potwierdz')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
